// Self-hosted webfonts, served from the widget's own origin and injected into
// the Shadow DOM stylesheet. This avoids loading fonts.googleapis.com /
// fonts.gstatic.com, which strict third-party host CSPs block. The host already
// trusts the widget origin (it loaded our script from there), so these requests
// pass. Every family a config can select through `FONT_STACKS` must be bundled
// here, each subsetted into latin + latin-ext to cover Czech diacritics.
//
// `font-display: block` (rather than `swap`) keeps text invisible until the
// font arrives instead of painting it in a fallback and re-rendering a moment
// later. The configurator is branded UI where that swap reads as a glitch, and
// the files are small and same-origin, so the block period stays short.

const LATIN_RANGE =
  "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";

const LATIN_EXT_RANGE =
  "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF";

const SUBSETS = [
  { name: "latin", range: LATIN_RANGE },
  { name: "latin-ext", range: LATIN_EXT_RANGE },
] as const;

type BundledFont = {
  // CSS family name, matching the one used in `FONT_STACKS`.
  family: string;
  // File name prefix under `fonts/`.
  prefix: string;
  // One entry per bundled file group. `file` is the file name segment, `css`
  // the `font-weight` descriptor. Static families ship one file per weight
  // (`poppins-400-latin.woff2`); variable families ship a single file per
  // subset covering a weight range (`exo2-variable-latin.woff2`).
  weights: readonly { file: string; css: string }[];
};

const BUNDLED_FONTS: readonly BundledFont[] = [
  {
    family: "Poppins",
    prefix: "poppins",
    // Only the weights actually used by the UI are bundled.
    weights: [200, 400, 500, 700].map((weight) => ({
      file: String(weight),
      css: String(weight),
    })),
  },
  {
    family: "Exo 2",
    prefix: "exo2",
    weights: [{ file: "variable", css: "100 900" }],
  },
];

/**
 * Builds @font-face rules for every bundled family, with absolute URLs against
 * `baseUrl`. Absolute URLs are required because url() in a Shadow DOM
 * stylesheet resolves against the host document, not the widget bundle.
 *
 * All families are declared up front, before the element knows its config: a
 * declaration alone costs nothing, browsers fetch a font file only once the
 * page actually renders text with it.
 */
export function buildBundledFontFaceCss(baseUrl: string): string {
  return BUNDLED_FONTS.flatMap((font) =>
    font.weights.flatMap((weight) =>
      SUBSETS.map((subset) => {
        const fontUrl = new URL(
          `fonts/${font.prefix}-${weight.file}-${subset.name}.woff2`,
          baseUrl,
        ).toString();
        return `@font-face {
  font-family: "${font.family}";
  font-style: normal;
  font-weight: ${weight.css};
  font-display: block;
  src: url("${fontUrl}") format("woff2");
  unicode-range: ${subset.range};
}`;
      }),
    ),
  ).join("\n");
}
